
import { Observable } from 'rxjs';
import { ZipcodeError } from '../model/zipcode-error';
import { ZipcodeErrorCode } from '../model/zipcode-error-code';
import { UFS_VALIDAS } from '../model/constants';

const throwCepError = (error: ZipcodeErrorCode) => {
  throw new ZipcodeError(error);
};

const ehStringValida = (data: string): boolean => !!data && data.trim() !== '';

const hasMinLength = (data: string, minLength: number): boolean =>
  data.trim().length >= minLength;

const hasMaxLength = (data: string, maxLength: number): boolean =>
  data.trim().length <= maxLength;

const validarMunicipio = (municipio: string): void => {
  if (!ehStringValida(municipio)) {
    throwCepError(ZipcodeErrorCode.MUNICIPIO_VAZIO);
  }

  if (!hasMinLength(municipio, 3)) {
    throwCepError(ZipcodeErrorCode.MUNICIPIO_MUITO_CURTO);
  }
};

const validarLogradouro = (logradouro: string): void => {
  if (!ehStringValida(logradouro)) {
    throwCepError(ZipcodeErrorCode.LOGRADOURO_VAZIO);
  }

  if (!hasMinLength(logradouro, 3)) {
    throwCepError(ZipcodeErrorCode.LOGRADOURO_MUITO_CURTO);
  }
};

const validarUF = (uf: string): void => {
  if (!ehStringValida(uf)) {
    throwCepError(ZipcodeErrorCode.UF_VAZIA);
  }

  if (!hasMinLength(uf, 2)) {
    throwCepError(ZipcodeErrorCode.UF_MUITO_CURTA);
  }

  if (!hasMaxLength(uf, 2)) {
    throwCepError(ZipcodeErrorCode.UF_MUITO_LONGA);
  }

  if (!UFS_VALIDAS.some((it) => it.toLowerCase() === uf.toLowerCase())) {
    throwCepError(ZipcodeErrorCode.UF_NAO_EXISTE);
  }
};

export const validarCEP =
  () =>
  (source: Observable<string>): Observable<string> =>
    new Observable((subscriber) =>
      source.subscribe({
        next: (cep) => {
          try {
            const regex = new RegExp(/^[0-9]+$/);
            if (!ehStringValida(cep)) {
              throwCepError(ZipcodeErrorCode.CEP_VAZIO);
            }
            if (!regex.test(cep)) {
              throwCepError(ZipcodeErrorCode.CEP_INVALIDO);
            }
            if (cep.length < 8) {
              throwCepError(ZipcodeErrorCode.CEP_MUITO_CURTO);
            }
            if (cep.length > 8) {
              throwCepError(ZipcodeErrorCode.CEP_MUITO_LONGO);
            }
            subscriber.next(cep.trim());
          } catch (e) {
            subscriber.error(e);
          }
        },
        error: (error) => subscriber.error(error),
        complete: () => subscriber.complete(),
      })
    );

interface EnderecoPesquisa {
  uf: string;
  logradouro: string;
  municipio: string;
}

export const validarEndereco =
  () =>
  (source: Observable<EnderecoPesquisa>): Observable<EnderecoPesquisa> =>
    new Observable((subscriber) =>
      source.subscribe({
        next: ({ uf, logradouro, municipio }) => {
          try {
            validarUF(uf);
            validarMunicipio(municipio);
            validarLogradouro(logradouro);
          } catch (e) {
            subscriber.error(e);
          }
          subscriber.next({ uf, logradouro, municipio });
        },
        error: (error) => subscriber.error(error),
        complete: () => subscriber.complete(),
      })
    );
